import * as React from 'react';
import {
    ContextualMenu,
    IContextualMenuItem,
    IContextualMenuProps,
    DirectionalHint
} from 'office-ui-fabric-react/lib/ContextualMenu';
import { connectToCommandSets } from '../commandSet/CommandSet';
import { ICommand, ICommandStateProps } from '../../models/commands/ICommand';
import strings from './ActionContextMenu.resx';
import { IDecoratedProps } from '../../utilities/components/Decorators';
import { concat } from '../../utilities/arrays';

const NO_ITEMS: ICommand[] = [];

/**
 * @public
 */
export interface IActionContextMenuBaseProps {
    commands?: ICommand[];
    items?: IContextualMenuItem[];
    isOpen: boolean;
    directionalHint?: DirectionalHint;
}

/**
 * @public
 */
export type IActionContextMenuInjectedProps = Pick<IContextualMenuProps, 'items' | 'directionalHint'>;

/**
 * @public
 */
export type IActionContextMenuProps = IDecoratedProps<IContextualMenuProps, IActionContextMenuInjectedProps, IActionContextMenuBaseProps>;

/**
 * ActionContextMenu component which accepts 'commands' as input and return items to ContextualMenu component.
 * @public
 */
export const ActionContextMenu: React.ComponentType<IActionContextMenuProps> =
    connectToCommandSets((props: IActionContextMenuProps) => {
        return { main: props.commands };
    }, (menuItems: {
        main: ReadonlyArray<IContextualMenuItem & ICommandStateProps>;
    }, props: IActionContextMenuProps): IActionContextMenuInjectedProps => {
            const {
                items = NO_ITEMS,
                directionalHint = DirectionalHint.rightTopEdge
            } = props;

            return {
                items: concat(items, cleanContextMenuItems(menuItems.main, props)) as IContextualMenuItem[],
                directionalHint: directionalHint
            };
        })((props: IContextualMenuProps & {
            isOpen: boolean;
        }): JSX.Element | null => {
            const {
                isOpen,
                ...contextualMenuProps
            } = props;

            if (!isOpen) {
                return null;
            }

            return (
                <ContextualMenu { ...contextualMenuProps } />
            );
        });

function cleanContextMenuItems(
    items: ReadonlyArray<IContextualMenuItem & ICommandStateProps>,
    props: IActionContextMenuProps
): ReadonlyArray<IContextualMenuItem> {
    const {
        target
    } = props;

    return items.map((item: IContextualMenuItem & ICommandStateProps): IContextualMenuItem => {
        const {
            subMenuProps,
            onClick,
            hasLaunchTarget,
            ...ctxItem
        } = item;

        return {
            ...ctxItem,
            name: item.iconProps && item.iconProps.iconName === 'More' ? strings.More : item.name,
            icon: undefined,
            iconProps: undefined,
            subMenuProps: subMenuProps ? {
                ...subMenuProps,
                items: cleanContextMenuItems(subMenuProps.items, props) as IContextualMenuItem[]
            } : undefined,
            onClick: hasLaunchTarget && onClick ? (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                if (ev && target) {
                    // Some commands require that their event target be the original element on which
                    // the menu was launched. Override the event's own target with an element extracted
                    // from this menu's target.
                    if (target instanceof Event) {
                        ev.target = target.target as HTMLElement;
                    } else if (target instanceof Element) {
                        ev.target = target;
                    }
                }

                onClick(ev);
            } : onClick
        };
    });
}
